import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { getToursForContext, tourMatches } from '@/constants/tours/tours';
import { markTourPrompted } from '@/lib/tour-progress';
import { startTour } from '@/redux/slices/tourSlice';

const PROMPT_DELAY = 1200;

/**
 * The first time someone opens a screen that has a tour (after the welcome was offered), a
 * toast offers it once. Tours never start on their own.
 */
export default function useTourPrompts({ tours, progress, userKey, ready, context }) {
  const dispatch = useDispatch();
  const activeTourId = useSelector(state => state.tour.activeTourId);
  // One offer at a time: the most specific tour for this screen not yet taken or offered.
  // Area tours are offered on their own page only, not on pages they pass through.
  const tour = getToursForContext(tours, context).find(
    t =>
      t.prompt !== false &&
      (t.match || t.path === context.pathname) &&
      !progress.tours[t.id] &&
      !progress.prompted[t.id]
  );
  const due = ready && tour && progress.welcomeSeen && !activeTourId;
  const toastIdRef = React.useRef(null);
  const offeredRef = React.useRef(null);
  const contextRef = React.useRef(context);
  contextRef.current = context;

  // An offer belongs to the screen it was made on: moving to another page or wizard step
  // clears it, so "Take the tour" can't start a tour over the wrong screen.
  React.useEffect(() => {
    if (toastIdRef.current && offeredRef.current && !tourMatches(offeredRef.current, context)) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, [context]);

  // Starting any tour (from the offer, Help or the palette) clears an offer still on screen.
  React.useEffect(() => {
    if (activeTourId && toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, [activeTourId]);

  React.useEffect(() => {
    if (!due) return undefined;
    const timer = setTimeout(() => {
      markTourPrompted(userKey, tour.id);
      offeredRef.current = tour;
      toastIdRef.current = toast('Want a quick tour of this page?', {
        description: `${tour.title} · ${tour.description}`,
        duration: 8000,
        closeButton: true,
        action: {
          label: 'Take the tour',
          onClick: () => tourMatches(tour, contextRef.current) && dispatch(startTour(tour.id)),
        },
      });
    }, PROMPT_DELAY);
    return () => clearTimeout(timer);
  }, [due, tour, userKey, dispatch]);
}
